import ErrorIcon from "@mui/icons-material/Error";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  AlertTitle,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import isEqual from "lodash.isequal";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  calculateFreedayAllotments,
  fetchLatestBidAllotmentEventsByReferenceNumber,
  fetchLatestBidGroupEmpEventsByReferenceNumber,
} from "../../api/bid.js";
import { fetchEmployee } from "../../api/employee.js";
import { getPresignedUrl, uploadFileToS3 } from "../../api/system";
import PageLoading from "../../components/PageLoading.jsx";
import BidScheduledStatusChip from "../../components/bid/BidScheduledStatusChip.jsx";
import BidTimer from "../../components/bid/BidTimer.jsx";
import CreateFreedayBidGroup from "../../components/bid/CreateFreedayBidGroup.jsx";
import FreedayBidAward from "../../components/bid/FreedayBidAward.jsx";
import ManageFreedayAllotments from "../../components/bid/ManageFreedayAllotments.jsx";
import ManageFreedayBidChoices from "../../components/bid/ManageFreedayBidChoices";
import SetUpFreedayBid from "../../components/bid/SetUpFreedayBid.jsx";
import ConfirmButton from "../../components/fundamentals/ConfirmButton";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog.jsx";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../../components/fundamentals/SuccessDialog";
import { selectStationFilter, selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { clearFreedayAwardData } from "../../util/bid/bidAward.js";
import {
  getCurrentFormScheduledStatus,
  getS3ObjectName,
  isAwardedBid,
  isClosedBid,
  isDeletedBid,
  isOpenBid,
  isPendingBid,
  longPollingBidFetch,
} from "../../util/bid/bidCommon.js";
import { BID_SCHEDULED_STATUS, BID_STATUS, FREEDAY_BID_AWARD_TYPE } from "../../util/bid/bidConstants";
import { buildFreedayBidAllotmentsPayload, buildTimeOffBidS3Data } from "../../util/bid/bidEventBuilder";
import { getEligibleEmployees, getEmployeeUnrankedCount } from "../../util/bid/bidGroupUtils";
import {
  validateBidEffectiveDates,
  validateBidName,
  validateBidWindowTime,
  validateFreedayBidWindowDates,
} from "../../util/bid/bidInputValidation.js";
import {
  buildPayloadForAllotmentCalculation,
  calcReliefAllotmentInitialState,
  calcStandardAllotmentInitialState,
  generateDayStateRows,
  getReliefEmployeesCount,
  getStandardEmployeesCount,
  getTotalReliefAllotments,
  getTotalStandardAllotments,
} from "../../util/bid/freedayBidUtils.js";
import {
  getStandardReliefMissingCount,
  validateReliefAllotments,
  validateStandardAllotments,
} from "../../util/bid/freedayValidation.js";
import {
  getDayStateForReliefAllotment,
  getDayStateForStandardAllotment,
  getFormattedAllotmentDayState,
} from "../../util/calendar.js";
import { copyJsonObject } from "../../util/commonUtils.js";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  CONFIRM_TITLE,
  FEATURES,
  HOME_PATH,
  MANAGE_TIMEOFF_BID_TABS,
  SELECT_FREEDAY_BID_EFFECTIVE_MONTH_LIST,
} from "../../util/constants.js";
import { padTime } from "../../util/dateTimeFormatter.js";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  REFERENCE_NUMBER_SUB_TITLE,
} from "../../util/messageConstants";
import {
  getReferenceNumberFromHref,
  isBidWindowLessThanSevenDays,
  prepareBaseBidEventPayload,
  addScheduledStatusToBidEvent,
  handleWindowDateTimeChange,
  handleWindowDateChange as handleWindowDateChangeUtil,
} from "../../util/bid/manageBidPageUtils.js";

const ManageFreedayBidPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const [isBidGroupLoading, setIsBidGroupLoading] = useState(true);
  const [isBidAllotmentsLoading, setIsBidAllotmentsLoading] = useState(false);
  const stationFilter = useSelector(selectStationFilter);

  const [schedMonth, setSchedMonth] = useState("");
  const [schedYear, setSchedYear] = useState("");
  const [effectiveDatesErrors, setEffectiveDatesErrors] = useState("");
  const [windowDates, setWindowDates] = useState([null, null]);
  const [windowDatesErrors, setWindowDatesErrors] = useState("");
  const [windowDateHr, setWindowDateHr] = useState("");
  const [windowDateMin, setWindowDateMin] = useState("");
  const [windowDateTimeErrors, setWindowDateTimeErrors] = useState("");
  const [bidName, setBidName] = useState("");
  const [bidNameErrors, setBidNameErrors] = useState("");
  const [bidGroup, setBidGroup] = useState([]);
  const [initialBidGroup, setInitialBidGroup] = useState([]);
  const [isBidGroupManuallyLoaded, setIsBidGroupManuallyLoaded] = useState(false);

  const [calcStandardAllotments, setCalcStandardAllotments] = useState(
    copyJsonObject(calcStandardAllotmentInitialState)
  );
  const [calcReliefAllotments, setCalcReliefAllotments] = useState(copyJsonObject(calcReliefAllotmentInitialState));
  const [standardDayState, setStandardDayState] = useState([]);
  const [reliefDayState, setReliefDayState] = useState([]);
  const [initialStandardDayState, setInitialStandardDayState] = useState([]);
  const [initialReliefDayState, setInitialReliefDayState] = useState([]);
  // Dialog state
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [saveErrors, setSaveErrors] = useState("");
  const [isManageFreedayBidChoicesSelected, setIsManageFreedayBidChoicesSelected] = useState(false);
  const [selectedBidGroupEmployeeData, setSelectedBidGroupEmployeeData] = useState("");
  const [selectedAwardedBid, setSelectedAwardedBid] = useState(null);
  const [isAwardedNameSelected, setIsAwardedNameSelected] = useState(false);
  const [showEffectiveYearChangeWarningDialog, setShowEffectiveYearChangeWarningDialog] = useState(false);
  const [effectiveYearTempState, setEffectiveYearTempState] = useState("");

  const isInitialPageLoadRef = useRef(true);
  const initialBidState = useRef(null);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isReadyToBeScheduledByUser, setIsReadyToBeScheduledByUser] = useState(false);
  const [isSchedMonthOrYearChanged, setIsSchedMonthOrYearChanged] = useState(false);
  const [isBidGroupTabChanged, setIsBidGroupTabChanged] = useState(false);
  const [isBidAllotmentsTabChanged, setIsBidAllotmentsTabChanged] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  useEffect(() => {
    if (standardDayState.length > 0 && schedMonth !== "" && schedYear !== "" && bidGroup.length > 0) {
      setIsSchedMonthOrYearChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedMonth, schedYear]);

  useEffect(() => {
    setWindowDatesErrors(
      validateFreedayBidWindowDates(windowDates, schedMonth, schedYear, props.bidData.status, props.bidData.station)
    );
    // eslint-disable-next-line
  }, [windowDates, schedMonth, schedYear]);

  useEffect(() => {
    if (props.isDesktopView) {
      loadBidData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDesktopView]);

  useEffect(() => {
    if (schedMonth !== "" && schedYear !== "" && bidGroup.length > 0 && isBidGroupManuallyLoaded) {
      calculateAllotments(
        buildPayloadForAllotmentCalculation(
          props.bidData,
          schedYear,
          schedMonth,
          getStandardEmployeesCount(bidGroup),
          getReliefEmployeesCount(bidGroup)
        )
      );
      setIsBidGroupManuallyLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedMonth, schedYear, bidGroup, isBidGroupManuallyLoaded]);

  // set bid metadata fields into state
  useEffect(() => {
    if (props.bidData?.windowStartDate) {
      setWindowDates([props.bidData.windowStartDate, props.bidData.windowEndDate]);
      setWindowDateHr(padTime(props.bidData.windowStartDate.hour));
      setWindowDateMin(padTime(props.bidData.windowStartDate.minute));
    }

    if (props.bidData?.schedMonth) {
      setSchedMonth(props.bidData?.schedMonth);
    }

    if (props.bidData?.schedYear) {
      setSchedYear(props.bidData.schedYear);
    }

    if (props.bidData?.bidName) {
      setBidName(props.bidData.bidName);
    }

    setIsPageLoading(false);
  }, [props.bidData]);

  // useEffect for detecting isInitialPageLoad
  useEffect(() => {
    if (!isPageLoading && !isBidAllotmentsLoading && !isBidGroupLoading && isInitialPageLoadRef.current) {
      initialBidState.current = getKeyDiffData();
      isInitialPageLoadRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageLoading, isBidAllotmentsLoading, isBidGroupLoading]);

  useEffect(() => {
    if (!isInitialPageLoadRef.current) {
      setIsFormChanged(true);
    }
  }, [
    isFormChanged,
    windowDates,
    windowDateHr,
    windowDateMin,
    schedMonth,
    schedYear,
    standardDayState,
    reliefDayState,
    bidGroup,
  ]);

  const getBidAdminPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.BID_ADMINISTRATION).path;
  };

  const handleEmployeeChoicesAsAdmin = (employeeData) => {
    setSelectedBidGroupEmployeeData(employeeData);
    setIsManageFreedayBidChoicesSelected(true);
  };

  const handleAwardedNameClick = (awardedBid) => {
    setIsAwardedNameSelected(true);
    fetchEmployee(awardedBid.eid)
      .then((res) => res.json())
      .then((response) => {
        if (response) {
          setSelectedBidGroupEmployeeData(response);
          setSelectedAwardedBid(awardedBid);
        } else {
          console.error("ERROR");
        }
      });
  };

  const loadBidData = () => {
    isInitialPageLoadRef.current = true;
    setIsFormChanged(false);
    setIsBidGroupLoading(true);
    setIsBidAllotmentsLoading(true);
    const referenceNumber = getReferenceNumberFromHref();
    fetchLatestBidGroupEmpEventsByReferenceNumber(userSession.eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          setBidGroup(response.events);
          setInitialBidGroup(response.events);
          setIsBidGroupLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Group Error: %j`, err);
        props.setLoadErrors(true);
        setBidGroup([]);
        setInitialBidGroup([]);
        setIsBidGroupLoading(false);
      });

    fetchLatestBidAllotmentEventsByReferenceNumber(userSession.eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          if (
            response.events.reliefAllotments &&
            response.events.standardAllotments &&
            response.events.calcStandardAllotments &&
            response.events.calcReliefAllotments
          ) {
            const reconstructedStandardAllotment = getFormattedAllotmentDayState(
              response.events.standardAllotments,
              props.bidData.schedMonth,
              props.bidData.schedYear
            );
            const reconstructedReliefAllotment = getFormattedAllotmentDayState(
              response.events.reliefAllotments,
              props.bidData.schedMonth,
              props.bidData.schedYear
            );
            setCalcStandardAllotments(response.events.calcStandardAllotments);
            setCalcReliefAllotments(response.events.calcReliefAllotments);
            setStandardDayState(reconstructedStandardAllotment);
            setReliefDayState(reconstructedReliefAllotment);
            setInitialStandardDayState(reconstructedStandardAllotment);
            setInitialReliefDayState(reconstructedReliefAllotment);
          }
          setIsBidAllotmentsLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Allotment Error: %j`, err);
        props.setLoadErrors(true);
        setStandardDayState([]);
        setReliefDayState([]);
        setInitialStandardDayState([]);
        setInitialReliefDayState([]);
        setCalcStandardAllotments(copyJsonObject(calcStandardAllotmentInitialState));
        setCalcReliefAllotments(copyJsonObject(calcReliefAllotmentInitialState));
        setIsBidAllotmentsLoading(false);
      });
  };

  const calculateAllotments = (payload) => {
    setIsBidAllotmentsLoading(true);
    calculateFreedayAllotments(payload)
      .then((res) => res.json())
      .then((response) => {
        setCalcStandardAllotments(response.calcStandardAllotments);
        setStandardDayState(getDayStateForStandardAllotment(schedYear, schedMonth, response.calcStandardAllotments));
        setReliefDayState(getDayStateForReliefAllotment(schedYear, schedMonth, response.calcReliefAllotments));
        setCalcReliefAllotments(response.calcReliefAllotments);
        setIsBidAllotmentsLoading(false);
        setIsSchedMonthOrYearChanged(false);
      })
      .catch((err) => {
        setCalcStandardAllotments(copyJsonObject(calcStandardAllotmentInitialState));
        setCalcReliefAllotments(copyJsonObject(calcReliefAllotmentInitialState));
        setIsBidAllotmentsLoading(false);
        setStandardDayState([]);
        setReliefDayState([]);
      });
  };

  const handleClickCancelDialogOpen = () => {
    setIsExitDialogOpen(true);
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const isSetUpFreedayBidTabInputValid = () => {
    return (
      effectiveDatesErrors === "" && windowDatesErrors === "" && windowDateTimeErrors === "" && bidNameErrors === ""
    );
  };

  const isAllotmentTabValid = () => {
    if (isBidAllotmentsLoading || isBidGroupLoading) {
      return true;
    }
    return !isSchedMonthOrYearChanged;
  };

  const handleEffectiveMonthChange = (event) => {
    setEffectiveDatesErrors(validateBidEffectiveDates(event.target.value, schedYear));
    setSchedMonth(event.target.value);
  };

  const handleEffectiveYearChange = (event) => {
    if (bidGroup.length > 0) {
      setShowEffectiveYearChangeWarningDialog(true);
      setEffectiveYearTempState(event.target.value);
      return;
    }

    handleEffectiveYearChangeConfirm(event);
  };

  const handleEffectiveYearChangeConfirm = (event) => {
    // change in effective year should clear the bidgroup since we need to fetch new bidgroup based on new year declarations
    if (bidGroup.length > 0) {
      setBidGroup([]);
      setShowEffectiveYearChangeWarningDialog(false);
    }

    setEffectiveDatesErrors(validateBidEffectiveDates(schedMonth, event?.target?.value || effectiveYearTempState));
    setSchedYear(event?.target?.value || effectiveYearTempState);
    setSchedMonth("");
    setEffectiveYearTempState("");
  };

  const handleEffectiveYearChangeCancel = () => {
    setShowEffectiveYearChangeWarningDialog(false);
    setEffectiveYearTempState("");
  };

  const handleWindowDateChange = (event) => {
    setWindowDatesErrors(
      validateFreedayBidWindowDates(event, schedMonth, schedYear, props.bidData.status, props.bidData.station)
    );
    const newWindowDates = handleWindowDateChangeUtil(event, windowDates, windowDateHr, windowDateMin);
    setWindowDates(newWindowDates);
  };

  const handleBidWindowHourChange = (event) => {
    setWindowDateTimeErrors(
      validateBidWindowTime(windowDates, event.target.value, windowDateMin, stationFilter, props.bidData.status)
    );
    const newWindowDates = handleWindowDateTimeChange(windowDates, event.target.value, "hour");
    setWindowDates(newWindowDates);
    setWindowDateHr(event.target.value);
  };

  const handleBidWindowMinuteChange = (event) => {
    setWindowDateTimeErrors(
      validateBidWindowTime(windowDates, windowDateHr, event.target.value, stationFilter, props.bidData.status)
    );
    const newWindowDates = handleWindowDateTimeChange(windowDates, event.target.value, "minute");
    setWindowDates(newWindowDates);
    setWindowDateMin(event.target.value);
  };

  const handleBidNameChange = (event) => {
    setBidNameErrors(validateBidName(event.target.value));
    setBidName(event.target.value);
  };

  const handleBidGroupChange = (event) => {
    setBidGroup(event);
    setIsBidGroupTabChanged(true);
  };

  const handleStandardDayStateChange = (event) => {
    setStandardDayState(event);
    setIsBidAllotmentsTabChanged(true);
  };

  const handleReliefDayStateChange = (event) => {
    setReliefDayState(event);
    setIsBidAllotmentsTabChanged(true);
  };

  const handleManageFreedayBidBackClick = () => {
    setSelectedBidGroupEmployeeData("");
    setSelectedAwardedBid(null);
    setIsAwardedNameSelected(false);
  };

  const isAwardTabAvailable = () => {
    return props.bidData.status === BID_STATUS.CLOSED || props.bidData.status === BID_STATUS.AWARDED;
  };

  const getSchedulableValidationErrors = () => {
    let validationErrors = [];

    if (!schedMonth || !schedYear) {
      validationErrors.push("Bid Effective Month and Year are missing");
    }

    if (!windowDates[0] || !windowDates[1] || !windowDateHr || !windowDateMin) {
      validationErrors.push("Bid Window Dates are missing");
    }

    const unrankedEmployeeCount = getEmployeeUnrankedCount(getEligibleEmployees(bidGroup));
    if (unrankedEmployeeCount > 0) {
      if (unrankedEmployeeCount === 1) {
        validationErrors.push(`${unrankedEmployeeCount} Employee is unranked`);
      } else {
        validationErrors.push(`${unrankedEmployeeCount} Employees are unranked`);
      }
    }
    if (
      getEligibleEmployees(bidGroup).length === 0 &&
      getTotalStandardAllotments(standardDayState) + getTotalReliefAllotments(reliefDayState) === 0
    ) {
      validationErrors.push("Allotments and Eligible Employees are missing");
    }

    const standardReliefMissingCount = getStandardReliefMissingCount(bidGroup);
    if (standardReliefMissingCount > 0) {
      validationErrors.push("Missing Standard Relief");
    }
    return validationErrors;
  };

  const getHardStopValidationErrors = () => {
    let validationErrors = [];
    if (isSchedMonthOrYearChanged) {
      validationErrors.push(
        "The Bid Effective Month/Year has changed. Please click Recalculate to update the allotments."
      );
    }

    if (isOpenBid(props.bidData.status) || isClosedBid(props.bidData.status)) {
      // unranked employees found
      const unrankedEmployeeCount = getEmployeeUnrankedCount(getEligibleEmployees(bidGroup));
      if (unrankedEmployeeCount > 0) {
        if (unrankedEmployeeCount === 1) {
          validationErrors.push(`${unrankedEmployeeCount} Employee is unranked`);
        } else {
          validationErrors.push(`${unrankedEmployeeCount} Employees are unranked`);
        }
      }

      // some employee's standardRelief flag is not set correctly
      const standardReliefMissingCount = getStandardReliefMissingCount(bidGroup);
      if (standardReliefMissingCount > 0) {
        validationErrors.push("Missing Standard Relief");
      }
    }
    //Allotment mismatch validations are Hard validations in bid closed status
    if (isClosedBid(props.bidData.status)) {
      //standard allotments mismatch found
      const standardAllotmentErrors = validateStandardAllotments(
        getStandardEmployeesCount(bidGroup),
        getTotalStandardAllotments(standardDayState)
      );
      if (standardAllotmentErrors) {
        validationErrors.push(standardAllotmentErrors);
      }

      // relief allotments mismatch found
      const reliefAllotmentErrors = validateReliefAllotments(
        getReliefEmployeesCount(bidGroup),
        calcReliefAllotments,
        schedMonth,
        schedYear
      );
      if (reliefAllotmentErrors) {
        validationErrors.push(reliefAllotmentErrors);
      }
    }
    return validationErrors;
  };

  const getSoftValidationErrors = () => {
    let validationErrors = [];

    //standard allotments mismatch found
    const standardAllotmentErrors = validateStandardAllotments(
      getStandardEmployeesCount(bidGroup),
      getTotalStandardAllotments(standardDayState)
    );
    if (standardAllotmentErrors) {
      validationErrors.push(standardAllotmentErrors);
    }

    // relief allotments mismatch found
    const reliefAllotmentErrors = validateReliefAllotments(
      getReliefEmployeesCount(bidGroup),
      calcReliefAllotments,
      schedMonth,
      schedYear
    );
    if (reliefAllotmentErrors) {
      validationErrors.push(reliefAllotmentErrors);
    }

    return validationErrors;
  };

  const renderScheduledSection = () => {
    const schedulableValidationErrors = getSchedulableValidationErrors();
    const hardStopValidationErrors = getHardStopValidationErrors();
    return (
      <>
        {hardStopValidationErrors.length > 0 && (
          <>
            <Typography sx={{ marginTop: "30px" }}>Please fix the following before the bid can be saved:</Typography>
            {hardStopValidationErrors.map((validationError) => {
              return (
                <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
                  <ErrorIcon sx={{ marginRight: "5px", color: "primary.swaRed" }} />
                  {validationError}
                </Typography>
              );
            })}
          </>
        )}
        {schedulableValidationErrors.length > 0 && (
          <>
            <Typography sx={{ marginTop: "30px" }}>
              Please fix the following before the bid can be scheduled:
            </Typography>
            {schedulableValidationErrors.map((validationError) => {
              return (
                <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
                  <WarningIcon sx={{ marginRight: "5px", color: "primary.swaYellow" }} />
                  {validationError}
                </Typography>
              );
            })}
          </>
        )}
        <FormControlLabel
          label="Bid is ready to be scheduled"
          control={
            <Checkbox
              checked={isReadyToBeScheduledByUser}
              onChange={() => setIsReadyToBeScheduledByUser(!isReadyToBeScheduledByUser)}
              disabled={schedulableValidationErrors.length > 0 || hardStopValidationErrors.length > 0 || isSaveLoading}
            />
          }
          sx={{ marginTop: "10px", marginLeft: "1px" }}
        ></FormControlLabel>
      </>
    );
  };

  const handleClickSaveDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSaveDialogOpen(false);
  };

  const handleClickSubmitDialogOpen = () => {
    if (!isFormValidationSuccess()) {
      setIsSaveDialogOpen(false);
      return;
    }
    setIsReadyToBeScheduledByUser(false);
    setIsSaveDialogOpen(true);
  };

  const isFormValidationSuccess = () => {
    const localBidEffectiveDatesError = validateBidEffectiveDates(schedMonth, schedYear);
    setEffectiveDatesErrors(localBidEffectiveDatesError);
    const localWindowDateErrors = validateFreedayBidWindowDates(
      windowDates,
      schedMonth,
      schedYear,
      props.bidData.status,
      props.bidData.station
    );
    setWindowDatesErrors(localWindowDateErrors);

    const localWindowDateTimeErrors = validateBidWindowTime(
      windowDates,
      windowDateHr,
      windowDateMin,
      stationFilter,
      props.bidData.status
    );
    setWindowDateTimeErrors(localWindowDateTimeErrors);

    let localBidNameErrors = "";
    // doing bid name validations only in pending state due to exiting bids prior to STARTEC-2820 having special characters
    if (isPendingBid(props.bidData.status)) {
      localBidNameErrors = validateBidName(bidName);
      setBidNameErrors(localBidNameErrors);
    }

    return (
      localBidEffectiveDatesError === "" &&
      localWindowDateErrors === "" &&
      localWindowDateTimeErrors === "" &&
      localBidNameErrors === ""
    );
  };

  const renderHardStopErrorSection = () => {
    const validationErrors = getHardStopValidationErrors();
    if (validationErrors.length > 0) {
      return validationErrors.map((validationError) => {
        return (
          <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
            <ErrorIcon sx={{ marginRight: "5px", color: "primary.swaRed" }} />
            {validationError}
          </Typography>
        );
      });
    }
  };

  const renderSoftValidationErrorSection = () => {
    const validationErrors = getSoftValidationErrors();
    if (validationErrors.length > 0) {
      return validationErrors.map((validationError) => {
        return (
          <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
            <WarningIcon sx={{ marginRight: "5px", color: "primary.swaYellow" }} />
            {validationError}
          </Typography>
        );
      });
    }
  };

  const getUnAwardedList = (bidGroup) => {
    return bidGroup
      .filter(
        (emp) =>
          (emp.awardType === FREEDAY_BID_AWARD_TYPE.NO_BID ||
            emp.awardType === FREEDAY_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES ||
            emp.awardType === FREEDAY_BID_AWARD_TYPE.NO_SCHEDULE) &&
          (!emp.awardedDays || emp.awardedDays.length === 0)
      )
      .map((emp, idx) => ({ ...emp, id: idx }));
  };

  const prepareNewBidEventPayload = () => {
    const baseBidEvent = prepareBaseBidEventPayload(
      props.bidData,
      bidName,
      schedMonth,
      schedYear,
      windowDates,
      SELECT_FREEDAY_BID_EFFECTIVE_MONTH_LIST
    );
    return addScheduledStatusToBidEvent(baseBidEvent, props.bidData.status, isReadyToBeScheduledByUser);
  };

  const getKeyDiffData = () => {
    let keyDiffData = {
      bidMetadata: {
        schedMonth: schedMonth,
        schedYear: schedYear,
        windowDates: windowDates,
        windowDateHr: windowDateHr,
        windowDateMin: windowDateMin,
        bidName: bidName,
      },
      bidAllotments: buildFreedayBidAllotmentsPayload(
        standardDayState,
        reliefDayState,
        calcStandardAllotments,
        calcReliefAllotments
      ),
      bidGroup,
    };
    keyDiffData = copyJsonObject(keyDiffData);
    return keyDiffData;
  };

  const isBidDataChanged = () => {
    if (!isInitialPageLoadRef.current) {
      const isKeyDataChanged = !isEqual(initialBidState.current, getKeyDiffData());
      let isScheduledStatusChanged = false;
      if (isPendingBid(props.bidData.status)) {
        if (props.bidData.scheduledStatus !== getCurrentFormScheduledStatus(isReadyToBeScheduledByUser))
          isScheduledStatusChanged = true;
      }
      return isKeyDataChanged || isScheduledStatusChanged;
    }
  };

  const onSaveSuccess = () => {
    setIsSaveDialogOpen(false);
    setIsSaveLoading(false);
    setIsBidAllotmentsTabChanged(false);
    setIsBidGroupTabChanged(false);
    setIsSuccessDialogOpen(true);
  };

  const onSaveError = () => {
    setSaveErrors("Unexpected Error");
    setIsSaveDialogOpen(false);
    setIsSaveLoading(false);
    setIsSuccessDialogOpen(true);
  };

  const renderSaveFreedayBidDialog = () => {
    return (
      <Dialog open={isSaveDialogOpen} TransitionComponent={SlideUpTransition} keepMounted fullWidth>
        <DialogTitle>Save changes</DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"}>
            <Grid container>
              <Grid item xs={12}>
                <Typography sx={{ color: "black", marginBottom: "10px" }}>
                  You are saving changes made to this Freeday Bid.
                  {isSaveLoading ? (
                    <Typography display={"inline"}>
                      {" "}
                      This may take a few minutes. Your patience is appreciated.{" "}
                      <Typography display={"inline"} sx={{ color: "black" }}>
                        🙂
                      </Typography>
                    </Typography>
                  ) : (
                    ""
                  )}
                </Typography>
              </Grid>
            </Grid>
          </DialogContentText>
          {(isPendingBid(props.bidData.status) || isOpenBid(props.bidData.status)) &&
            renderSoftValidationErrorSection()}
          {isPendingBid(props.bidData.status) && (
            <>
              {isBidWindowLessThanSevenDays(windowDates) ? (
                <Alert severity="warning" sx={{ marginBottom: "30px" }}>
                  <AlertTitle>
                    <strong>WARNING</strong>
                  </AlertTitle>
                  You have entered a Bid Window with a duration less than 7 days.
                </Alert>
              ) : (
                ""
              )}
              {renderScheduledSection()}
            </>
          )}
          {(isOpenBid(props.bidData.status) || isClosedBid(props.bidData.status)) && renderHardStopErrorSection()}
        </DialogContent>
        <DialogActions>
          <ConfirmButton
            variant="outlined"
            color="primary"
            onClick={handleClickSaveDialogClose}
            disabled={isSaveLoading}
          >
            Cancel
          </ConfirmButton>
          <ConfirmButton
            variant="contained"
            color="primary"
            onClick={handleSaveClicked}
            isLoading={isSaveLoading}
            disabled={
              (isPendingBid(props.bidData.status) ||
                isOpenBid(props.bidData.status) ||
                isClosedBid(props.bidData.status)) &&
              getHardStopValidationErrors().length > 0
            }
          >
            Save
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const handleSaveClicked = async () => {
    if (!isFormValidationSuccess()) {
      setIsSaveDialogOpen(false);
      return;
    }
    setIsSaveLoading(true);
    let isAwardDataCleared = false;
    let currentStandardDayState = standardDayState;
    let currentReliefDayState = reliefDayState;
    let currentBidGroup = bidGroup;
    if (isBidAllotmentsTabChanged || isBidGroupTabChanged) {
      const clearedDataResult = clearFreedayAwardData(standardDayState, reliefDayState, bidGroup);
      currentStandardDayState = clearedDataResult.standardDayState;
      currentReliefDayState = clearedDataResult.reliefDayState;
      currentBidGroup = clearedDataResult.bidGroup;
      setStandardDayState(clearedDataResult.standardDayState);
      setReliefDayState(clearedDataResult.reliefDayState);
      setBidGroup(clearedDataResult.bidGroup);
      isAwardDataCleared = true;
    }

    // Save Bid Data to S3
    try {
      if (!isBidDataChanged() && !isAwardDataCleared) {
        // If no changes just show success message and exit
        onSaveSuccess();
        return;
      }

      const s3ObjectName = await getS3ObjectName(props.bidData.pk, props.bidData.station);
      const allotmentsPayload = buildFreedayBidAllotmentsPayload(
        currentStandardDayState,
        currentReliefDayState,
        calcStandardAllotments,
        calcReliefAllotments
      );
      const res = await getPresignedUrl("put_object", "bid-data", s3ObjectName);
      const result = await res.json();

      if (result.presignedUrl) {
        const bidS3Data = buildTimeOffBidS3Data(
          userSession,
          prepareNewBidEventPayload(),
          allotmentsPayload,
          currentBidGroup,
          s3ObjectName
        );
        const response = await uploadFileToS3(result.presignedUrl, bidS3Data);

        if (response.status === 200) {
          const requestId = response.headers.get("x-amz-request-id");
          props.currentRequestId.current = requestId;
          await longPollingBidFetch(props.bidData.referenceNumber, userSession, requestId);
          onSaveSuccess();
        } else {
          const errorMessage = "Bad response from uploadFileToS3";
          console.error(errorMessage);
          throw Error(errorMessage);
        }
      } else {
        const errorMessage = "Bad response from getPresignedUrl";
        console.error(errorMessage);
        throw Error(errorMessage);
      }
    } catch (err) {
      console.error("Error during handleSaveClicked: ", err);
      onSaveError();
    }
  };

  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.BID_ADMINISTRATION,
    BREADCRUMBS_TITLE.MANAGE_FREE_DAY_BID,
  ];
  const breadcrumbsLinks = [HOME_PATH, getBidAdminPath()];
  const renderDesktopManageFreedayBidPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {isManageFreedayBidChoicesSelected ? (
          <>
            <ManageFreedayBidChoices
              selectedBidGroupEmployeeData={selectedBidGroupEmployeeData}
              referenceNumber={getReferenceNumberFromHref()}
              featureConfig={props.featureConfig}
              setIsManageFreedayBidChoicesSelected={setIsManageFreedayBidChoicesSelected}
              station={props.bidData.station}
              isDesktopView={props.isDesktopView}
              setIsBidGroupTabChanged={setIsBidGroupTabChanged}
            />
          </>
        ) : isAwardedNameSelected ? (
          <ManageFreedayBidChoices
            selectedBidGroupEmployeeData={selectedBidGroupEmployeeData}
            referenceNumber={getReferenceNumberFromHref()}
            featureConfig={props.featureConfig}
            setIsManageFreedayBidChoicesSelected={setIsManageFreedayBidChoicesSelected}
            station={props.bidData.station}
            isDesktopView={props.isDesktopView}
            handleManageFreedayBidBackClick={handleManageFreedayBidBackClick}
            isAwardedEmpReadOnlyView={true}
            selectedAwardedBid={selectedAwardedBid}
          />
        ) : (
          <>
            <StarsBreadcrumbs
              breadcrumbsList={breadcrumbsList}
              breadcrumbsLinks={breadcrumbsLinks}
              isConfirmationRequired={isFormChanged}
              cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
            />
            {/* Title & Cancel/Submit Buttons */}
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{ display: "flex", justifyContent: "start", paddingTop: "10px", paddingLeft: "2rem" }}
              >
                <Typography variant="h5" component="span">
                  Manage Freeday Bid
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "2rem" }}>
                <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                  <Button
                    sx={{ width: "100px" }}
                    variant="outlined"
                    color="primary"
                    onClick={handleClickCancelDialogOpen}
                  >
                    Cancel
                  </Button>
                </Box>

                <Box sx={{ paddingLeft: "10px", paddingTop: "10px" }}>
                  <Button
                    sx={{ width: "100px" }}
                    variant="contained"
                    color="primary"
                    onClick={handleClickSubmitDialogOpen}
                    disabled={
                      isDeletedBid(props.bidData.status) ||
                      isAwardedBid(props.bidData.status) ||
                      props.loadErrors ||
                      isBidGroupLoading ||
                      isBidAllotmentsLoading
                    }
                  >
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: "left", marginLeft: "32px" }}>
              <Typography variant="body2" component="span" sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" component="span" sx={{ display: "inline-block" }}>
                  Status:
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: "bold", display: "inline-block", marginLeft: "5px" }}
                >
                  {props.bidData.status}
                </Typography>
                <Box sx={{ display: "inline-block", marginLeft: "5px" }}>
                  <BidScheduledStatusChip scheduledStatus={props.bidData.scheduledStatus} />
                </Box>
                {props.bidData.status !== BID_STATUS.DELETED &&
                  !(
                    props.bidData.status === BID_STATUS.PENDING &&
                    props.bidData.scheduledStatus === BID_SCHEDULED_STATUS.DELAYED
                  ) &&
                  windowDates[0] !== null &&
                  windowDates[1] !== null &&
                  windowDateHr &&
                  windowDateMin && (
                    <>
                      <Typography variant="body2" component="span" sx={{ marginLeft: "5px", marginRight: "5px" }}>
                        |
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ color: "primary.swaRed", fontWeight: "bold", display: "inline-block" }}
                      >
                        <BidTimer windowDates={windowDates} station={props.bidData.station} />
                      </Typography>
                    </>
                  )}
              </Typography>
            </Box>
            {/* The TABS! */}
            <Box sx={{ width: "100%", margin: "auto", paddingLeft: "2rem", paddingRight: "2rem", marginTop: "1rem" }}>
              <Tabs
                value={props.selectedTab}
                onChange={props.handleChangeTab}
                sx={{ width: "inherit", margin: "auto", bgcolor: "secondary.swaGray1" }}
                variant="fullWidth"
              >
                <Tab
                  sx={{ fontWeight: "bold", height: "70px" }}
                  label={MANAGE_TIMEOFF_BID_TABS.SET_UP_THE_TIMEOFF_BID}
                  icon={
                    !isSetUpFreedayBidTabInputValid() ? (
                      <ErrorOutlineIcon sx={{ color: "primary.swaRed" }} />
                    ) : undefined
                  }
                  iconPosition="start"
                ></Tab>
                <Tab sx={{ fontWeight: "bold" }} label={MANAGE_TIMEOFF_BID_TABS.MANAGE_BID_GROUP}></Tab>
                <Tab
                  sx={{ fontWeight: "bold" }}
                  label={MANAGE_TIMEOFF_BID_TABS.MANAGE_ALLOTMENTS}
                  icon={!isAllotmentTabValid() ? <ErrorOutlineIcon sx={{ color: "primary.swaRed" }} /> : undefined}
                  iconPosition="start"
                ></Tab>
                {isAwardTabAvailable() && <Tab sx={{ fontWeight: "bold" }} label={MANAGE_TIMEOFF_BID_TABS.AWARD}></Tab>}
              </Tabs>

              {props.selectedTab === 0 && (
                <div>
                  <SetUpFreedayBid
                    bid={props.bidData}
                    schedMonth={schedMonth}
                    setSchedMonth={handleEffectiveMonthChange}
                    schedYear={schedYear}
                    setSchedYear={handleEffectiveYearChange}
                    effectiveDatesErrors={effectiveDatesErrors}
                    windowDates={windowDates}
                    setWindowDates={handleWindowDateChange}
                    windowDatesErrors={windowDatesErrors}
                    windowDateHr={windowDateHr}
                    setWindowDateHr={handleBidWindowHourChange}
                    windowDateMin={windowDateMin}
                    setWindowDateMin={handleBidWindowMinuteChange}
                    windowDateTimeErrors={windowDateTimeErrors}
                    bidName={bidName}
                    bidNameErrors={bidNameErrors}
                    setBidNameChange={handleBidNameChange}
                  />
                </div>
              )}

              {props.selectedTab === 1 && (
                <div>
                  <CreateFreedayBidGroup
                    bid={props.bidData}
                    bidGroup={bidGroup}
                    isBidGroupLoading={isBidGroupLoading}
                    setBidGroup={handleBidGroupChange}
                    schedMonth={schedMonth}
                    schedYear={schedYear}
                    windowDates={windowDates}
                    handleEmployeeChoicesAsAdmin={handleEmployeeChoicesAsAdmin}
                    initialBidGroup={initialBidGroup}
                    userSession={userSession}
                    setIsBidGroupManuallyLoaded={setIsBidGroupManuallyLoaded}
                    setCalcStandardAllotments={setCalcStandardAllotments}
                    setCalcReliefAllotments={setCalcReliefAllotments}
                    setStandardDayState={setStandardDayState}
                    setReliefDayState={setReliefDayState}
                    setIsSchedMonthOrYearChanged={setIsSchedMonthOrYearChanged}
                    bidName={bidName}
                  />
                </div>
              )}

              {props.selectedTab === 2 && (
                <div>
                  {
                    <ManageFreedayAllotments
                      bid={props.bidData}
                      bidGroup={bidGroup}
                      schedMonth={schedMonth}
                      schedYear={schedYear}
                      isBidAllotmentsLoading={isBidAllotmentsLoading}
                      isBidGroupLoading={isBidGroupLoading}
                      calcStandardAllotments={calcStandardAllotments}
                      setCalcStandardAllotments={setCalcStandardAllotments}
                      standardDayState={standardDayState}
                      setStandardDayState={handleStandardDayStateChange}
                      calcReliefAllotments={calcReliefAllotments}
                      setCalcReliefAllotments={setCalcReliefAllotments}
                      reliefDayState={reliefDayState}
                      setReliefDayState={handleReliefDayStateChange}
                      isSchedMonthOrYearChanged={isSchedMonthOrYearChanged}
                      setIsSchedMonthOrYearChanged={setIsSchedMonthOrYearChanged}
                      bidName={bidName}
                    />
                  }
                </div>
              )}

              {isAwardTabAvailable() && props.selectedTab === 3 && (
                <div>
                  <FreedayBidAward
                    bid={props.bidData}
                    setBid={props.setBidData}
                    bidGroup={bidGroup}
                    isBidGroupLoading={isBidGroupLoading}
                    setBidGroup={setBidGroup}
                    isBidGroupTabChanged={isBidGroupTabChanged}
                    bidName={bidName}
                    unAwardedList={getUnAwardedList(bidGroup)}
                    freedayAwardRows={generateDayStateRows(
                      initialStandardDayState,
                      initialReliefDayState,
                      initialBidGroup
                    )}
                    bidAdminPath={getBidAdminPath()}
                    isFormChanged={isFormChanged}
                    loadBidData={props.reloadBidData}
                    standardDayState={standardDayState}
                    setStandardDayState={setStandardDayState}
                    reliefDayState={reliefDayState}
                    setReliefDayState={setReliefDayState}
                    isBidAllotmentsLoading={isBidAllotmentsLoading}
                    isBidAllotmentsTabChanged={isBidAllotmentsTabChanged}
                    calcStandardAllotments={calcStandardAllotments}
                    calcReliefAllotments={calcReliefAllotments}
                    schedMonth={schedMonth}
                    schedYear={schedYear}
                    handleAwardedNameClick={handleAwardedNameClick}
                    setInitialStandardDayState={setInitialStandardDayState}
                    setInitialReliefDayState={setInitialReliefDayState}
                    setInitialBidGroup={setInitialBidGroup}
                    currentRequestId={props.currentRequestId}
                  />
                </div>
              )}
            </Box>

            <ConfirmationDialog
              isDialogOpen={isExitDialogOpen}
              title={CONFIRM_TITLE}
              message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)}
              handleNoButtonClicked={handleClickCancelDialogClose}
              redirectTo={getBidAdminPath()}
            />
            {isSaveDialogOpen && renderSaveFreedayBidDialog()}
            <SuccessDialog
              isSuccessDialogOpen={isSuccessDialogOpen}
              title={"Bid Confirmation"}
              subTitle={REFERENCE_NUMBER_SUB_TITLE(props.bidData.referenceNumber)}
              message={"The bid has been saved!"}
              hasError={saveErrors !== ""}
              error={saveErrors}
              handleOKClick={() => {
                setSaveErrors("");
                setIsSuccessDialogOpen(false);
                props.reloadBidData();
              }}
            />
            <ConfirmationDialog
              isDialogOpen={showEffectiveYearChangeWarningDialog}
              title={"Warning"}
              message={"Changing the bid effective year will clear the bid group. Do you want to proceed?"}
              handleNoButtonClicked={handleEffectiveYearChangeCancel}
              handleYesButtonClicked={handleEffectiveYearChangeConfirm}
              noTitle="No"
              yesTitle="YES"
            />
          </>
        )}
      </>
    );
  };

  const renderMobileManageFreedayBidPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  return (
    <div className="App-contents enable-scroll" data-testid="manage-freeday-bid-page">
      {props.isDesktopView ? renderDesktopManageFreedayBidPage() : renderMobileManageFreedayBidPage()}
    </div>
  );
};

ManageFreedayBidPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  bidData: PropTypes.object.isRequired,
  selectedTab: PropTypes.number.isRequired,
  handleChangeTab: PropTypes.func.isRequired,
  setLoadErrors: PropTypes.func.isRequired,
  currentRequestId: PropTypes.object.isRequired,
};

export default ManageFreedayBidPage;
